/**
 * DrugForm component for displaying and managing drug information
 * Provides different screens for drug overview, advisories, and care areas
 * @module DrugForm
 */
import { Drug } from 'interfaces/drug';
import { FC, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import DrugService from 'services/DrugService';
import DrugFormOverview from './DrugFormOverview/DrugFormOverview';
import ListAdvisories from './ListAdvisories/ListAdvisories';
import { Facility } from 'interfaces/facility';
import CopyDrugCareAreas from './CopyDrugCareAreas/CopyDrugCareAreas';
import { ScreenEnum } from '../enums';

/**
 * Props for the DrugForm component
 * @typedef {Object} DrugFormProps
 * @property {Drug} drug - The drug object to be displayed and managed
 * @property {Facility} facility - The facility information related to the drug
 */
type DrugFormProps = {
  drug: Drug;
  facility: Facility;
};

/**
 * @description Component for displaying and managing drug information with multiple screens (overview, advisories, care areas).
 * Fetches and manages drug state with different views for drug configuration.
 *
 * @SADtrace SAD-003-4.1, SAD-003-4.2
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @param {DrugFormProps} props - Component props
 *
 * @returns {JSX.Element} Rendered component
 */
const DrugForm: FC<DrugFormProps> = ({ drug, facility }) => {
  const { t } = useTranslation();

  const [record, setRecord] = useState<Drug | null>(null);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [formLoading, setFormLoading] = useState(false);
  const [screen, setScreen] = useState<ScreenEnum>(ScreenEnum.Overview);

  /**
   * @description Fetches drug data by ID and updates the component state.
   * Shows error toast if loading fails.
   *
   * @SADtrace SAD-003-4.1, SAD-003-4.2
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   *
   * @param {string | number} id - The ID of the drug to fetch
   *
   * @returns {void}
   */
  const getDrug = (id: string | number) => {
    DrugService.getDrug(id)
      .then(({ data }) => {
        setRecord(data);
        setFormLoading(false);
      })
      .catch((_) => {
        setFormLoading(false);
        toast.error(t('device.messages.drug_can_not_be_loaded'));
      });
  };

  /**
   * @description Effect hook to fetch drug data when the drug prop changes
   */
  useEffect(() => {
    setRecord(null);
    setFormLoading(true);
    setScreen(ScreenEnum.Overview);
    getDrug(drug.id);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drug]);

  return (
    <div className="pt-3 pb-5" data-testid="drug-form-component">
      <div className="device-concentration-form__drug--name pb-5">
        {drug.name}
      </div>
      {!!record && screen === ScreenEnum.Overview && (
        <DrugFormOverview
          drug={record}
          setScreen={setScreen}
          updateDrug={getDrug}
        />
      )}
      {!!record &&
        screen === ScreenEnum.Advisories && ( // !record.is_basic
          <ListAdvisories
            drug={record}
            setScreen={setScreen}
            updateDrug={getDrug}
          />
        )}
      {!!record && !record.is_basic && screen === ScreenEnum.CareAreas && (
        <CopyDrugCareAreas
          drug={record}
          facility={facility}
          canManage={true}
          setScreen={setScreen}
        />
      )}
    </div>
  );
};

export default DrugForm;
